import React, { useContext } from "react";
import { Helmet } from "react-helmet-async";
import { Box, Container, Typography, Link as MuiLink } from "@mui/material";
import Header from "components/Header/Header.js";
import HomeFooter from "components/Footer/HomeFooter.js";
import HeaderLinks from "components/Header/HeaderLinks.js";
import { ThemeContext } from "components/Theme/ThemeContext";
import { useTranslation } from "react-i18next";

export default function DeleteAccount(props) {
  const { isDark, currentTheme } = useContext(ThemeContext);
  const { i18n } = useTranslation();
  const isRTL = i18n?.language === 'ar' || i18n?.language === 'ara' || (typeof i18n?.language === 'string' && i18n.language.startsWith('ar'));
  const { ...rest } = props;

  return (
    <Box sx={{ bgcolor: currentTheme.background, display: 'flex', flexDirection: 'column', transition: 'background-color 0.3s ease, color 0.3s ease', direction: isRTL ? 'rtl' : 'ltr' }}>
      <Helmet>
        <title>{isRTL ? 'حذف الحساب | زحل قطر' : 'Delete Your Account | Rahl Qatar'}</title>
      </Helmet>
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Header
          color="transparent"
          routes={[]}
          rightLinks={<HeaderLinks />}
          fixed
          changeColorOnScroll={{
            height: 100,
            color: isDark ? "dark" : "white"
          }}
          {...rest}
        />

        {/* Hero Section */}
        <Box sx={{
          background: isDark ? 'linear-gradient(135deg, #0A0A0B 0%, #161619 100%)' : 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)',
          color: isDark ? 'white' : '#0F172A',
          pt: 20,
          pb: 8,
          textAlign: 'center'
        }}>
          <Container maxWidth="md">
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, fontFamily: 'Outfit, sans-serif' }}>
              {isRTL ? 'حذف الحساب والبيانات' : 'Delete Your Account & Data'}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.7, color: isDark ? '#94a3b8' : '#475569' }}>
              {isRTL ? 'آخر تحديث: سبتمبر 2026' : 'Last Updated: September 2026'}
            </Typography>
          </Container>
        </Box>

        {/* Content Section */}
        <Container maxWidth="md" sx={{ py: 8 }}>
          <Box sx={{ color: isDark ? '#cbd5e1' : '#334155', textAlign: isRTL ? 'right' : 'left', '& p': { mb: 3, lineHeight: 1.8 }, '& h3': { mt: 4, mb: 2, fontWeight: 700, color: isDark ? 'white' : '#0f172a' } }}>

            {isRTL ? (
              <>
                <Typography variant="body1">
                  إذا كنت تمتلك حساباً على تطبيق زحل (Rahl) - سواء تم إنشاؤه برقم الهاتف، أو البريد الإلكتروني، أو تسجيل الدخول عبر جوجل - يمكنك طلب حذف حسابك والبيانات المرتبطة به في أي وقت.
                </Typography>

                <Typography variant="h6" component="h3">
                  كيفية تقديم طلب الحذف
                </Typography>
                <Typography variant="body1">
                  أرسل بريداً إلكترونياً إلى <MuiLink href="mailto:support@rahl.me" sx={{ color: '#E9AE31', textDecoration: 'none', fontWeight: 600 }}>support@rahl.me</MuiLink> من عنوان بريدك الإلكتروني المسجل، أو اتصل بنا على <MuiLink href="tel:+97431395694" sx={{ color: '#E9AE31', textDecoration: 'none', fontWeight: 600 }}>+974 31395694</MuiLink>، مع ذكر:
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 1, lineHeight: 1.8 } }}>
                  <li>رقم الهاتف أو البريد الإلكتروني المسجل في حسابك</li>
                  <li>طلب صريح لحذف الحساب والبيانات المرتبطة به</li>
                </Box>
                <Typography variant="body1">
                  سنتحقق من هويتك، ثم نعالج طلبك خلال ثلاثين (30) يوماً تقويمياً كحد أقصى.
                </Typography>

                <Typography variant="h6" component="h3">
                  ما الذي يتم حذفه
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 1, lineHeight: 1.8 } }}>
                  <li><strong>بيانات الحساب:</strong> الاسم، رقم الهاتف، البريد الإلكتروني، وسجل التسجيل يتم حذفها أو إخفاء هويتها بشكل دائم.</li>
                  <li><strong>رموز الإشعارات (Push Tokens):</strong> تُزال فوراً من أنظمتنا فور معالجة الطلب.</li>
                </Box>

                <Typography variant="h6" component="h3">
                  ما الذي قد يتم الاحتفاظ به
                </Typography>
                <Typography variant="body1">
                  قد نحتفظ بسجلات المعاملات المالية أو الفواتير للفترة التي يقتضيها القانون المعمول به (لأغراض ضريبية أو محاسبية أو مكافحة الاحتيال)، حتى بعد حذف الحساب. لا تُستخدم هذه السجلات لأي غرض آخر بعد الحذف.
                </Typography>

                <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                  لمزيد من التفاصيل حول كيفية جمعنا واستخدامنا لبياناتك، يرجى مراجعة{' '}
                  <MuiLink href="/privacy-policy" sx={{ color: '#E9AE31', textDecoration: 'none', fontWeight: 600 }}>سياسة الخصوصية</MuiLink>.
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body1">
                  If you have an account with the Rahl app - created with your phone number, email address, or Google Sign-In - you can request deletion of your account and the data associated with it at any time.
                </Typography>

                <Typography variant="h6" component="h3">
                  How to request deletion
                </Typography>
                <Typography variant="body1">
                  Email <MuiLink href="mailto:support@rahl.me" sx={{ color: '#E9AE31', textDecoration: 'none', fontWeight: 600 }}>support@rahl.me</MuiLink> from your registered email address, or call us at <MuiLink href="tel:+97431395694" sx={{ color: '#E9AE31', textDecoration: 'none', fontWeight: 600 }}>+974 31395694</MuiLink>, with:
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 1, lineHeight: 1.8 } }}>
                  <li>The phone number or email address registered on your account</li>
                  <li>A clear statement that you want your account and associated data deleted</li>
                </Box>
                <Typography variant="body1">
                  We'll verify your identity and process the request within thirty (30) calendar days.
                </Typography>

                <Typography variant="h6" component="h3">
                  What gets deleted
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 1, lineHeight: 1.8 } }}>
                  <li><strong>Account data:</strong> Your name, phone number, email address, and registration records are permanently deleted or anonymized.</li>
                  <li><strong>Push notification tokens:</strong> Removed from our systems immediately once the request is processed.</li>
                </Box>

                <Typography variant="h6" component="h3">
                  What may be retained
                </Typography>
                <Typography variant="body1">
                  We may retain payment or invoicing records for as long as required by applicable law (for tax, accounting, or fraud-prevention purposes), even after your account is deleted. These records are not used for any other purpose after deletion.
                </Typography>

                <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                  For more detail on how we collect and use your data, see our{' '}
                  <MuiLink href="/privacy-policy" sx={{ color: '#E9AE31', textDecoration: 'none', fontWeight: 600 }}>Privacy Policy</MuiLink>.
                </Typography>
              </>
            )}

          </Box>
        </Container>
      </Box>
      <HomeFooter />
    </Box>
  );
}
